import { type Report, useStores } from '@littlebird/sdk';
import { isRoutineRunUnread } from '@littlebird/ui-utils';
import { useMemo } from 'react';

import {
  routineReadState,
  useRoutineRefresh,
} from '@/features/routines/use-routine-refresh';
import { useVisibleRoutines } from '@/features/settings/proactive-agent.query';

const getReportTimestamp = (report?: Report) => {
  if (!report) return 0;

  const scheduledAt = report.scheduledAt.getTime();
  if (!Number.isNaN(scheduledAt)) return scheduledAt;

  const createdAt = report.createdAt.getTime();
  return Number.isNaN(createdAt) ? 0 : createdAt;
};

export function useActiveRoutines() {
  // The loading flags are MobX state React Compiler cannot see change, so a
  // memoized return would keep reporting loading after the routines loaded
  'use no memo';
  const { routines: routineStore, reports: reportStore } = useStores();
  useRoutineRefresh();
  const routines = useVisibleRoutines(routineStore.userRoutines);
  const reports = reportStore.orderedReports;

  const latestReportByRoutineId = useMemo(() => {
    const latestReportByRoutineId = new Map<number, Report>();

    for (const report of reports) {
      const currentReport = latestReportByRoutineId.get(
        report.reportTemplateId
      );
      if (
        !currentReport ||
        getReportTimestamp(report) > getReportTimestamp(currentReport)
      ) {
        latestReportByRoutineId.set(report.reportTemplateId, report);
      }
    }

    return latestReportByRoutineId;
  }, [reports]);
  const isRoutineUnread = (routineId: number) => {
    const latestReport = latestReportByRoutineId.get(routineId);
    return (
      latestReport !== undefined &&
      isRoutineRunUnread(latestReport.text, latestReport.lastViewed !== null)
    );
  };
  const activeRoutines = useMemo(() => {
    const originalOrder = new Map(
      routines.map((routine, index) => [routine.referenceId, index])
    );

    return routines
      .filter(
        (routine) =>
          !routine.isPaused && !routine.isExperimental && !routine.completedAt
      )
      .sort((a, b) => {
        const reportDifference =
          getReportTimestamp(latestReportByRoutineId.get(b.referenceId)) -
          getReportTimestamp(latestReportByRoutineId.get(a.referenceId));
        if (reportDifference !== 0) return reportDifference;

        return (
          (originalOrder.get(a.referenceId) ?? 0) -
          (originalOrder.get(b.referenceId) ?? 0)
        );
      });
  }, [latestReportByRoutineId, routines]);

  return {
    routines,
    activeRoutines,
    latestReportByRoutineId,
    isRoutineUnread,
    isLoading: !routineReadState.hasLoadedRoutines,
    areReportsLoading: !routineReadState.hasLoadedReports,
  };
}
